import type { CallReportDocument } from '~/types/callReportDocument'

export const ACCEPTED_DOCUMENT_TYPES = ['image/jpeg', 'image/png', 'application/pdf']
export const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024 // 10 MB
const BUCKET = 'documents'

export function useReportDocuments() {
    const supabase = useSupabaseClient()

    function validate(file: File): string | null {
        if (!ACCEPTED_DOCUMENT_TYPES.includes(file.type)) {
            return `${file.name}: only JPG, PNG, or PDF allowed.`
        }
        if (file.size > MAX_DOCUMENT_BYTES) {
            return `${file.name}: exceeds 10 MB limit.`
        }
        return null
    }

    async function listDocuments(reportId: string): Promise<CallReportDocument[]> {
        const { data, error } = await supabase
            .from('call_report_documents')
            .select('*')
            .eq('call_report_id', reportId)
            .order('created_at', { ascending: false })
            .returns<CallReportDocument[]>()
        if (error) throw error
        return data ?? []
    }

    async function uploadDocument(reportId: string, uploaderId: string, file: File): Promise<CallReportDocument> {
        const validationError = validate(file)
        if (validationError) throw new Error(validationError)

        const storagePath = `call-reports/${reportId}/${crypto.randomUUID()}-${file.name}`

        const { error: uploadError } = await supabase.storage
            .from(BUCKET)
            .upload(storagePath, file, { contentType: file.type })
        if (uploadError) throw uploadError

        const { data, error: insertError } = await supabase
            .from('call_report_documents')
            .insert({
                call_report_id: reportId,
                uploaded_by: uploaderId,
                file_name: file.name,
                storage_path: storagePath,
                mime_type: file.type,
                size_bytes: file.size,
            } as never)
            .select('*')
            .returns<CallReportDocument[]>()
            .single()

        if (insertError || !data) {
            // Roll back the orphaned object so storage doesn't drift from the table.
            await supabase.storage.from(BUCKET).remove([storagePath])
            throw insertError ?? new Error('Failed to record document.')
        }
        return data
    }

    async function getSignedUrl(storagePath: string): Promise<string> {
        const { data, error } = await supabase.storage
            .from(BUCKET)
            .createSignedUrl(storagePath, 60)
        if (error || !data) throw error ?? new Error('Could not create link.')
        return data.signedUrl
    }

    async function deleteDocument(doc: CallReportDocument): Promise<void> {
        // Delete the row first: a failed storage delete only orphans an invisible
        // object, whereas a failed row delete would leave a row pointing at a
        // missing file (a broken "view" link).
        const { error: rowError } = await supabase
            .from('call_report_documents')
            .delete()
            .eq('id', doc.id)
        if (rowError) throw rowError
        const { error: storageError } = await supabase.storage.from(BUCKET).remove([doc.storage_path])
        if (storageError) throw storageError
    }

    return { validate, listDocuments, uploadDocument, getSignedUrl, deleteDocument, ACCEPTED_DOCUMENT_TYPES, MAX_DOCUMENT_BYTES }
}
